import { useState, useEffect, useCallback } from "react";
import { useCustomers } from "@/contexts/CustomerContext";
import { useAuth } from "@/contexts/AuthContext";
import { apiRequest } from "@/lib/api";
import { Send, Zap, Edit2, Check, X, Clock, MessageSquare, Smartphone } from "lucide-react";
import "./FollowUps.css";

interface FollowUpTask {
  id: string;
  customerId: string;
  customerName: string;
  phone: string;
  type: string;
  title: string;
  urgency: "high" | "medium" | "low";
  message: string;
  reason: string;
  suggestedDelay: string;
  status: "pending" | "sent" | "skipped";
}

export default function FollowUps() {
  const { customers } = useCustomers();
  const { user } = useAuth();
  const [tasks, setTasks] = useState<FollowUpTask[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [sentLog, setSentLog] = useState<{ name: string; time: string }[]>([]);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [sendingTaskId, setSendingTaskId] = useState<string | null>(null);

  const checkSmsConfig = useCallback(async () => {
    if (!user?.storeId) return;
    try {
      const data = await apiRequest<any>(`/sms/config?storeId=${user.storeId}`);
      setSmsEnabled(data.smsEnabled || false);
    } catch {}
  }, [user?.storeId]);

  useEffect(() => { checkSmsConfig(); }, [checkSmsConfig]);

  const generate = async () => {
    setIsGenerating(true);
    try {
      const data = await apiRequest<{ tasks: FollowUpTask[] }>("/followups/generate", {
        method: "POST",
        body: JSON.stringify({ customers }),
      });
      const withIds = (data.tasks || []).map((t: any, i: number) => {
        const cust = customers.find((c) => c.id === t.customerId);
        return {
          ...t,
          id: `task-${Date.now()}-${i}`,
          customerName: cust?.name || t.customerName || "Unknown",
          phone: cust?.phone || t.phone || "",
          reason: t.reason || t.reasoning || "",
          status: "pending" as const,
        };
      });
      setTasks(withIds);
    } catch (err: any) {
      alert("Failed to generate follow-ups: " + err.message);
    }
    setIsGenerating(false);
  };

  const sendSms = async (task: FollowUpTask) => {
    if (smsEnabled && user?.storeId) {
      setSendingTaskId(task.id);
      try {
        await apiRequest("/sms/send", {
          method: "POST",
          body: JSON.stringify({
            storeId: user.storeId,
            to: task.phone.startsWith("+") ? task.phone : `+1${task.phone.replace(/\D/g, "")}`,
            message: task.message,
            customerName: task.customerName,
            userId: user.id,
            userName: user.name,
          }),
        });
        setTasks(tasks.map((t) => (t.id === task.id ? { ...t, status: "sent" as const } : t)));
        setSentLog([{ name: task.customerName, time: new Date().toLocaleTimeString() }, ...sentLog]);
      } catch (err: any) {
        alert("Failed to send SMS: " + (err.message || "Unknown error"));
      }
      setSendingTaskId(null);
    } else {
      const body = encodeURIComponent(task.message);
      window.open(`sms:${task.phone}?body=${body}`, "_blank");
      setTasks(tasks.map((t) => (t.id === task.id ? { ...t, status: "sent" as const } : t)));
      setSentLog([{ name: task.customerName, time: new Date().toLocaleTimeString() }, ...sentLog]);
    }
  };

  const startEdit = (task: FollowUpTask) => {
    setEditingId(task.id);
    setEditText(task.message);
  };

  const saveEdit = (id: string) => {
    setTasks(tasks.map((t) => (t.id === id ? { ...t, message: editText } : t)));
    setEditingId(null);
  };

  const urgencyColor = (u: string) => u === "high" ? "var(--danger)" : u === "medium" ? "var(--warning)" : "var(--success)";
  const pendingTasks = tasks.filter((t) => t.status === "pending");
  const completedTasks = tasks.filter((t) => t.status !== "pending");

  return (
    <div className="page">
      <div className="page-header">
        <div className="header-row">
          <div>
            <h1>Follow-Up Autopilot</h1>
            <p>AI-generated personalized follow-up messages for your customers</p>
          </div>
          <button className="btn btn-primary" onClick={generate} disabled={isGenerating}>
            <Zap size={16} /> {isGenerating ? "Generating..." : "Generate Follow-Ups"}
          </button>
        </div>
      </div>

      {isGenerating && <div className="loading-spinner" />}

      {!isGenerating && tasks.length === 0 && (
        <div className="card">
          <div className="empty-state">
            <Send size={40} style={{ marginBottom: 12, color: "var(--primary)" }} />
            <h3>Ready to Follow Up</h3>
            <p>Click "Generate Follow-Ups" to analyze your {customers.length} customers and create personalized messages</p>
          </div>
        </div>
      )}

      {pendingTasks.length > 0 && (
        <div className="tasks-section">
          <h3 className="section-title">Pending ({pendingTasks.length})</h3>
          <div className="tasks-grid">
            {pendingTasks.map((task) => (
              <div key={task.id} className="card task-card">
                <div className="task-header">
                  <div className="task-customer">
                    <div className="avatar-sm">{task.customerName.split(" ").map((n) => n[0]).join("")}</div>
                    <div>
                      <strong>{task.customerName}</strong>
                      <span className="task-phone">{task.phone}</span>
                    </div>
                  </div>
                  <div className="task-meta">
                    <span className="task-type">{task.type}</span>
                    <span className="urgency-dot" style={{ background: urgencyColor(task.urgency) }} title={task.urgency} />
                  </div>
                </div>
                <div className="task-reasoning">{task.reason}</div>
                {editingId === task.id ? (
                  <div className="task-edit">
                    <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={4} />
                    <div className="task-edit-actions">
                      <button className="btn btn-primary btn-sm" onClick={() => saveEdit(task.id)}><Check size={14} /> Save</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditingId(null)}><X size={14} /> Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="task-message">
                    <MessageSquare size={14} />
                    <p>{task.message}</p>
                  </div>
                )}
                <div className="task-actions">
                  <button className="btn btn-success btn-sm" onClick={() => sendSms(task)} disabled={sendingTaskId === task.id}>
                    {sendingTaskId === task.id ? <Clock size={14} /> : smsEnabled ? <Smartphone size={14} /> : <Send size={14} />}
                    {sendingTaskId === task.id ? " Sending..." : smsEnabled ? " Send via Twilio" : " Send SMS"}
                  </button>
                  {editingId !== task.id && (
                    <button className="btn btn-secondary btn-sm" onClick={() => startEdit(task)}><Edit2 size={14} /> Edit</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {completedTasks.length > 0 && (
        <div className="tasks-section">
          <h3 className="section-title">Completed ({completedTasks.length})</h3>
          <div className="tasks-grid">
            {completedTasks.map((task) => (
              <div key={task.id} className="card task-card completed">
                <div className="task-header">
                  <div className="task-customer">
                    <div className="avatar-sm">{task.customerName.split(" ").map((n) => n[0]).join("")}</div>
                    <strong>{task.customerName}</strong>
                  </div>
                  <span className={`status-badge status-${task.status === "sent" ? "active" : "closed"}`}>{task.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {sentLog.length > 0 && (
        <div className="card" style={{ marginTop: 20 }}>
          <h3 style={{ marginBottom: 12 }}>Audit Log</h3>
          <table>
            <thead><tr><th>Customer</th><th>Time</th><th>By</th></tr></thead>
            <tbody>
              {sentLog.map((log, i) => (
                <tr key={i}><td>{log.name}</td><td>{log.time}</td><td>{user?.name}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
